import ProductShowcaseComponent from './product-showcase-component';
import { MakeGraphQLQuery } from '../../utils/helpers';
import { getProductShowcaseGraphQLQuery } from '../../graphql/productShowcase.gql';
import { getProductsByIds } from '../../graphql/product.gql';
import { updateAllSampleButtons } from '../product-samples/product-samples';

export function getMetafieldValue(metafields, metafieldKey) {
  const metafield =
    metafields && metafields.find((item) => item?.key === metafieldKey);
  return metafield ? metafield.value : false;
}

function setProductCardDiscountBanner(productMetafields, shopMetafields) {
  const productDiscountObject = {
    text: getMetafieldValue(productMetafields, 'discount_message'),
    textColor: getMetafieldValue(productMetafields, 'discount_text_colour'),
    background: getMetafieldValue(productMetafields, 'discount_banner_colour'),
  };

  const globalDiscountObject = {
    text: getMetafieldValue(shopMetafields, 'g_discount_banner_message'),
    textColor: getMetafieldValue(
      shopMetafields,
      'g_discount_banner_text_colour'
    ),
    background: getMetafieldValue(shopMetafields, 'g_discount_banner_colour'),
  };

  const hasMetafieldValue = (value) =>
    value !== undefined && value !== null && !!value;
  const hasProductSpecificDiscount = Object.values(productDiscountObject).every(
    hasMetafieldValue
  );
  const hasGlobalSpecificDiscount =
    Object.values(globalDiscountObject).every(hasMetafieldValue);

  return hasProductSpecificDiscount
    ? productDiscountObject
    : hasGlobalSpecificDiscount
      ? globalDiscountObject
      : false;
}

async function assignSample(products) {
  products
    .filter((p) => p.node.title.toLocaleLowerCase().includes('sample'))
    .forEach((p) => {
      p.node.sampleId = p.node.variants.edges[0].node.id.replace('gid://shopify/ProductVariant/', '');
      p.node.sampleVariantIds = p.node.variants.edges.map((v) => v.node.id.replace('gid://shopify/ProductVariant/', '')).join(',');
      p.node.sampleHandle = p.node.handle;
      p.node.sampleTags = p.node.tags.join(',');
      p.node.sampleKit = true;
      p.node.sampleWeight = Number(getMetafieldValue(p.node.metafields, 'sample_kit_weight') || (p.node.sampleKit ? 3 : 1));
      p.node.kitOnly = true;
      // Check if sample kit product has disable_product_samples tag
      p.node.disableProductSamples = p.node.tags.includes('disable_product_samples');
    });

  const linkedSamples = [];
  const productMap = new Map();
  products.forEach((product) => {
    const sampleId = getMetafieldValue(
      product.node.metafields,
      'linked_sample'
    );
    if (sampleId) {
      linkedSamples.push(`"${sampleId}"`);
      productMap.set(sampleId, product.node);
    }
  });

  if (!linkedSamples.length) return;

  const queryStr = getProductsByIds(linkedSamples);

  try {
    const response = await MakeGraphQLQuery(
      `${window.theme.storeURL}/api/2026-04/graphql.json`,
      queryStr
    );

    if (response.hasOwnProperty('error')) {
      throw new Error('Failed GraphQL Request');
    }
    const sampleProducts = response.data.nodes;
    sampleProducts.forEach((sample) => {
      const originalProduct = productMap.get(sample.id);
      if (originalProduct) {
        originalProduct.sample = sample;
        originalProduct.sampleId = sample.variants?.edges?.[0]?.node?.id?.replace(
          'gid://shopify/ProductVariant/',
          ''
        );
        originalProduct.sampleVariantIds = sample.variants?.edges?.map((v) => v.node.id.replace('gid://shopify/ProductVariant/', '')).join(',');
        originalProduct.sampleHandle = sample.handle;
        originalProduct.sampleTags = '';
        originalProduct.sampleKit =
          getMetafieldValue(sample.metafields, 'is_sample_kit') === 'true';
        originalProduct.sampleWeight =
          Number(getMetafieldValue(sample.metafields, 'sample_kit_weight')) ||
          (originalProduct.sampleKit ? 3 : 1);
      }
    });
  } catch (error) {
    console.error('Error fetching samples:', error);
  }
}

// Fetch the Products onChange
export async function fetchProductsOnChange(query, findShopifyProductID) {
  try {
    // Create Empty Array and build a new object
    const response = await MakeGraphQLQuery(
      `${window.theme.storeURL}/api/2026-04/graphql.json`,
      query
    );

    if (response.hasOwnProperty('error')) {
      throw new Error('Failed GraphQL Request');
    }

    const products =
      response.data?.collections?.edges[0]?.node?.products?.edges;
    const shopMetafields = response.data?.shop?.metafields;
    // Push New Product Data
    // Shopify Encodes Product IDs base64
    // Using a VueJS filter to run A to B to decode
    if (products) {
      await assignSample(products);
      const queriedProducts = products
        // filter out products that don't have a sample for showcase
        .filter((product) => product.node.sampleId)
        .map((product) => {
          try {
            const {
              id,
              handle,
              tags,
              title,
              variants,
              images,
              options,
              metafields: productMetafields,
              sample,
              sampleId,
              sampleKit,
              sampleWeight,
              sampleHandle,
              sampleTags,
              sampleVariantIds,
              kitOnly = false,
              disableProductSamples: productDisableProductSamples = false,
            } = product.node;

            // Check if parent product has disable_product_samples tag
            // Use existing value for kitOnly products, or check tags for products with linked samples
            const disableProductSamples = productDisableProductSamples || (tags && tags.includes('disable_product_samples'));

            const productCardObject = {
              id: findShopifyProductID(btoa(id)),
              handle: handle,
              tags: tags,
              title: title,
              variants: variants.edges,
              images: images.edges,
              price: variants.edges[0].node.priceV2,
              qtyProductOption: options.filter(
                (option) => option.name == 'Qty'
              ),
              compareAtPrice: variants.edges[0].node.compareAtPriceV2,
              discountBanner: setProductCardDiscountBanner(
                productMetafields,
                shopMetafields
              ),
              metafields: productMetafields,
              sample,
              sampleId,
              sampleKit,
              sampleWeight,
              sampleHandle,
              sampleTags,
              sampleVariantIds,
              kitOnly,
              disableProductSamples,
            };
            return productCardObject;
          } catch (error) {
            console.error(error);
          }
        });
      return queriedProducts;
    }
  } catch (err) {
    console.error('ERROR', err.name, err.message);
  }
}

// 1. Client can use three different collections responding to the choice of the user Male, Female, Carers
// 2. Depending on Collection return Collection JSON to allow us to re render the page.
export default () => {
  if (!$('#product-showcase-dropdown').length) {
    return false;
  }

  const selectors = {
    productCarousel: '[data-product-carousel]',
    productCarouselDropdown: '#product-showcase-dropdown',
    productCarouselDefaultSlider: '#productShowcaseDefaultSlider',
    productCarouselVueSlider: '#productShowcaseVueSlider',
  };

  if ($(selectors.productCarouselDefaultSlider).length) {
    /**
     * Initialising the Product Showcase Vue Instance
     */
    const productShowcaseComponent = ProductShowcaseComponent(selectors);

    /**
     * Call a function that fetches product data when the dropdown changes
     */
    $(selectors.productCarouselDropdown).on('change', function () {
      // This Returns PRODUCTS JSON in that selected collection
      // Need to use this to re render the product.showcase section
      const selectedOptionValue = $(
        `${selectors.productCarouselDropdown} option:selected`
      ).val();

      // Fade Out UI
      $(selectors.productCarousel).fadeTo('fast', 0.25);

      // Create GraphQL Query based on Selected Options
      const collectionQuery =
        getProductShowcaseGraphQLQuery(selectedOptionValue);

      // Fetch Products using GraphQL API
      const findShopifyProductID =
        productShowcaseComponent.$options.filters.shopifyProductID;

      fetchProductsOnChange(collectionQuery, findShopifyProductID).then(
        (products) => {
          // Update Vue JS Component
          try {
            productShowcaseComponent.updateProductCards(products);
          } catch (error) {
            console.error(error);
          }
        }
      ).finally(() => {
        updateAllSampleButtons();
      });
    });

    /**
     * Calling the Compare Banner Components
     * Which have been set within the window object.
     */
    window.compare();
  }
};
